import {
  getLocalStorage,
  safeGetStorageItem,
  safeSetStorageItem,
} from './browserStorage';

const DEFAULT_MAX_WIDTH_RATIO = 0.95;
const DEFAULT_MIN_WIDTH = 400;
const DEFAULT_VIEWPORT_WIDTH = 1024;
const RIGHT_SIDEBAR_VISIBILITY_KEY = 'showRightSidebar';

const calculateMaxSidebarWidth = (
  windowWidth,
  maxWidthRatio = DEFAULT_MAX_WIDTH_RATIO
) => windowWidth * maxWidthRatio;

const getRightSidebarViewportWidth = (
  windowObject = typeof window !== 'undefined' ? window : null
) => {
  const viewportWidth = windowObject?.innerWidth;

  return Number.isFinite(viewportWidth) && viewportWidth > 0
    ? viewportWidth
    : DEFAULT_VIEWPORT_WIDTH;
};

const calculateRightSidebarViewportMaxWidth = ({
  maxWidthRatio = DEFAULT_MAX_WIDTH_RATIO,
  windowObject,
} = {}) =>
  calculateMaxSidebarWidth(
    getRightSidebarViewportWidth(windowObject),
    maxWidthRatio
  );

const resolveRightSidebarInitialVisibility = ({
  storage = getLocalStorage(),
} = {}) =>
  safeGetStorageItem(storage, RIGHT_SIDEBAR_VISIBILITY_KEY) !== 'false';

const persistRightSidebarVisibility = ({
  isVisible = true,
  storage = getLocalStorage(),
} = {}) => {
  if (!storage) {
    return true;
  }

  return safeSetStorageItem(
    storage,
    RIGHT_SIDEBAR_VISIBILITY_KEY,
    String(isVisible)
  );
};

const clampSidebarWidth = ({ maxWidth, minWidth = DEFAULT_MIN_WIDTH, width }) =>
  Math.min(Math.max(minWidth, width), maxWidth);

const getSidebarResizeClientX = (event) =>
  event?.touches?.[0]?.clientX ?? event?.clientX;

const buildSidebarResizeStartState = ({ event, sidebarWidth }) => ({
  startWidth: sidebarWidth,
  startX: getSidebarResizeClientX(event),
});

const buildSidebarResizeStartedState = ({ event, sidebarWidth } = {}) => ({
  ...buildSidebarResizeStartState({ event, sidebarWidth }),
  isResizing: true,
});

const buildSidebarResizeStoppedState = () => ({
  isResizing: false,
});

const calculateSidebarDragWidth = ({
  clientX,
  maxWidth,
  minWidth = DEFAULT_MIN_WIDTH,
  startWidth,
  startX,
}) => {
  const delta = startX - clientX;
  return clampSidebarWidth({
    maxWidth,
    minWidth,
    width: startWidth + delta,
  });
};

const calculateSidebarDragWidthFromEvent = ({
  event,
  maxWidth,
  minWidth = DEFAULT_MIN_WIDTH,
  startWidth,
  startX,
}) =>
  calculateSidebarDragWidth({
    clientX: getSidebarResizeClientX(event),
    maxWidth,
    minWidth,
    startWidth,
    startX,
  });

const runRightSidebarResizeStart = ({
  applyResizeState = null,
  event = null,
  setBodyCursor = null,
  sidebarWidth = 0,
} = {}) => {
  if (typeof applyResizeState !== 'function') {
    return { status: 'invalid' };
  }

  applyResizeState(buildSidebarResizeStartedState({ event, sidebarWidth }));
  if (typeof setBodyCursor === 'function') {
    setBodyCursor('ew-resize');
  }
  return { status: 'started' };
};

const runRightSidebarResizeDrag = ({
  emitResize = null,
  event = null,
  getMaxWidth = calculateRightSidebarViewportMaxWidth,
  isResizing = false,
  setSidebarWidth = null,
  startWidth = 0,
  startX = 0,
} = {}) => {
  if (!isResizing) {
    return { status: 'skipped' };
  }

  if (
    typeof emitResize !== 'function' ||
    typeof getMaxWidth !== 'function' ||
    typeof setSidebarWidth !== 'function'
  ) {
    return { status: 'invalid' };
  }

  const width = calculateSidebarDragWidthFromEvent({
    event,
    maxWidth: getMaxWidth(),
    startWidth,
    startX,
  });
  setSidebarWidth(width);
  emitResize(width);
  return { status: 'resized', width };
};

const runRightSidebarResizeStop = ({
  applyResizeState = null,
  setBodyCursor = null,
} = {}) => {
  if (typeof applyResizeState !== 'function') {
    return { status: 'invalid' };
  }

  applyResizeState(buildSidebarResizeStoppedState());
  if (typeof setBodyCursor === 'function') {
    setBodyCursor('');
  }
  return { status: 'stopped' };
};

const calculateAutoResizeTargetWidth = ({
  defaultWidth,
  maxWidth,
  sidebarWidth,
}) => Math.max(defaultWidth, Math.min(sidebarWidth / 2, maxWidth));

const runRightSidebarAutoResize = ({
  animateSidebarWidth = null,
  defaultWidth = 0,
  duration = 0,
  maxWidth = 0,
  sidebarWidth = 0,
} = {}) => {
  if (typeof animateSidebarWidth !== 'function') {
    return { status: 'invalid' };
  }

  const targetWidth = calculateAutoResizeTargetWidth({
    defaultWidth,
    maxWidth,
    sidebarWidth,
  });
  animateSidebarWidth(targetWidth, duration);
  return { status: 'animated', targetWidth };
};

const calculateHeaderResizeTargetWidth = ({
  defaultWidth,
  isExpanded,
  maxWidth,
}) => (isExpanded ? maxWidth : defaultWidth);

const runRightSidebarHeaderResize = ({
  animateSidebarWidth = null,
  defaultWidth = 0,
  duration = 0,
  isExpanded = false,
  maxWidth = 0,
} = {}) => {
  if (typeof animateSidebarWidth !== 'function') {
    return { status: 'invalid' };
  }

  const targetWidth = calculateHeaderResizeTargetWidth({
    defaultWidth,
    isExpanded,
    maxWidth,
  });
  animateSidebarWidth(targetWidth, duration);
  return { status: 'animated', targetWidth };
};

const runRightSidebarMaxWidthUpdate = ({
  applyMaxWidthState = null,
  getMaxWidth = calculateRightSidebarViewportMaxWidth,
} = {}) => {
  if (
    typeof applyMaxWidthState !== 'function' ||
    typeof getMaxWidth !== 'function'
  ) {
    return { status: 'invalid' };
  }

  const maxWidth = getMaxWidth();
  applyMaxWidthState({ maxWidth });
  return { maxWidth, status: 'applied' };
};

const calculateWideViewTargetWidth = ({ defaultWidth, isWide, wideWidth }) =>
  isWide ? wideWidth : defaultWidth;

const calculateRightSidebarLoadOlderScrollDelta = ({
  scrollHeightAfter = 0,
  scrollHeightBefore = 0,
} = {}) => {
  const after = Number.isFinite(scrollHeightAfter) ? scrollHeightAfter : 0;
  const before = Number.isFinite(scrollHeightBefore) ? scrollHeightBefore : 0;
  return after - before;
};

const runRightSidebarLoadOlderMessages = async ({
  container = null,
  currentThread = null,
  fetchOlderMessages = null,
  nextTick = null,
} = {}) => {
  if (!currentThread?.id) {
    return { status: 'skipped' };
  }

  if (
    typeof fetchOlderMessages !== 'function' ||
    typeof nextTick !== 'function'
  ) {
    return { status: 'invalid' };
  }

  const scrollHeightBefore = container?.scrollHeight || 0;
  await fetchOlderMessages({ threadId: currentThread.id });
  nextTick(() => {
    if (!container) {
      return;
    }

    container.scrollTop += calculateRightSidebarLoadOlderScrollDelta({
      scrollHeightAfter: container.scrollHeight,
      scrollHeightBefore,
    });
  });

  return { status: 'loaded' };
};

const resolveRightSidebarScrollState = ({
  clientHeight = 0,
  scrollHeight = 0,
  scrollTop = 0,
  threshold = 100,
} = {}) => ({
  showScrollButton: scrollHeight - scrollTop - clientHeight > threshold,
});

const runRightSidebarContentScroll = ({
  applyScrollState = null,
  container = null,
} = {}) => {
  if (typeof applyScrollState !== 'function') {
    return { status: 'invalid' };
  }

  if (!container) {
    return { status: 'missing-container' };
  }

  applyScrollState(
    resolveRightSidebarScrollState({
      clientHeight: container.clientHeight,
      scrollHeight: container.scrollHeight,
      scrollTop: container.scrollTop,
    })
  );
  return { status: 'applied' };
};

const shouldAutoResizeSidebarOnContentInsertion = ({
  defaultWidth,
  sidebarWidth,
} = {}) => sidebarWidth > defaultWidth;

const runRightSidebarContentInsertionResize = ({
  autoResizeSidebar = null,
  defaultWidth = 0,
  sidebarWidth = 0,
} = {}) => {
  if (typeof autoResizeSidebar !== 'function') {
    return { status: 'invalid' };
  }

  if (
    !shouldAutoResizeSidebarOnContentInsertion({
      defaultWidth,
      sidebarWidth,
    })
  ) {
    return { status: 'skipped' };
  }

  autoResizeSidebar();
  return { status: 'resized' };
};

const calculateAnimatedSidebarWidth = ({
  duration,
  elapsed,
  endWidth,
  startWidth,
}) => {
  const progress = Math.min(elapsed / duration, 1);
  const easeProgress = 1 - 2 ** (-10 * progress);

  return {
    progress,
    width: Math.round(startWidth + (endWidth - startWidth) * easeProgress),
  };
};

const buildRightSidebarWidthAnimationContext = ({
  component = null,
  emitResize = null,
  now = null,
  requestFrame = null,
  targetWidth = 0,
} = {}) => ({
  emitResize,
  now,
  requestFrame,
  setSidebarWidth: (width) => {
    if (component) {
      component.sidebarWidth = width;
    }
  },
  sidebarWidth: component?.sidebarWidth || 0,
  targetWidth,
});

const runRightSidebarWidthAnimation = ({
  duration = 0,
  emitResize = null,
  now = null,
  requestFrame = null,
  setSidebarWidth = null,
  sidebarWidth = 0,
  targetWidth = 0,
} = {}) => {
  if (
    typeof emitResize !== 'function' ||
    typeof now !== 'function' ||
    typeof requestFrame !== 'function' ||
    typeof setSidebarWidth !== 'function'
  ) {
    return { status: 'invalid' };
  }

  if (targetWidth === sidebarWidth) {
    return { status: 'skipped' };
  }

  const startWidth = sidebarWidth;
  const endWidth = targetWidth;
  const startTime = now();
  const animate = (currentTime) => {
    const elapsed = currentTime - startTime;
    const { progress, width } = calculateAnimatedSidebarWidth({
      duration,
      elapsed,
      endWidth,
      startWidth,
    });
    setSidebarWidth(width);
    if (progress < 1) {
      requestFrame(animate);
      return;
    }

    setSidebarWidth(endWidth);
    emitResize(endWidth);
  };

  requestFrame(animate);
  return { status: 'started' };
};

export {
  RIGHT_SIDEBAR_VISIBILITY_KEY,
  buildSidebarResizeStartedState,
  buildSidebarResizeStartState,
  buildSidebarResizeStoppedState,
  buildRightSidebarWidthAnimationContext,
  calculateAnimatedSidebarWidth,
  calculateAutoResizeTargetWidth,
  calculateHeaderResizeTargetWidth,
  calculateMaxSidebarWidth,
  calculateRightSidebarLoadOlderScrollDelta,
  calculateRightSidebarViewportMaxWidth,
  calculateSidebarDragWidth,
  calculateSidebarDragWidthFromEvent,
  calculateWideViewTargetWidth,
  getRightSidebarViewportWidth,
  getSidebarResizeClientX,
  persistRightSidebarVisibility,
  resolveRightSidebarInitialVisibility,
  resolveRightSidebarScrollState,
  runRightSidebarAutoResize,
  runRightSidebarContentScroll,
  runRightSidebarContentInsertionResize,
  runRightSidebarHeaderResize,
  runRightSidebarLoadOlderMessages,
  runRightSidebarMaxWidthUpdate,
  runRightSidebarResizeDrag,
  runRightSidebarResizeStart,
  runRightSidebarResizeStop,
  runRightSidebarWidthAnimation,
  shouldAutoResizeSidebarOnContentInsertion,
};
